<template>
  <div class="coin-history responsive-p">
    <p class="back" @click="$router.back()">&lt; {{ t('back') }}</p>
    <p class="title">{{ t('coinHistory.title') }}</p>

    <div class="balance-card">
      <img src="@/assets/icons/m-coin.svg" alt="coin" class="balance-icon" />
      <div>
        <p class="balance-amount">{{ numberWithCommas(totalCoin) }} Coins</p>
        <p class="balance-label">{{ t('coinHistory.balance') }}</p>
      </div>
    </div>

    <p v-if="expiringSoon" class="expiring-soon">
      {{ t('coinHistory.expiringSoon', {
        amount: numberWithCommas(expiringSoon.amount),
        date: formatDate(expiringSoon.expires_at),
      }) }}
    </p>

    <MiniLoading v-if="isLoading && !items.length" />

    <p v-else-if="!items.length" class="empty">{{ t('coinHistory.empty') }}</p>

    <div v-else class="entries">
      <div v-for="entry in items" :key="entry.id" class="entry">
        <div class="entry-main">
          <p class="entry-label">{{ t(`coinHistory.type.${entry.type}`) }}</p>
          <p v-if="describe(entry)" class="entry-detail">{{ describe(entry) }}</p>
          <p v-if="entry.expires_at" class="entry-expiry">
            {{ t('coinHistory.expiresOn', { date: formatDate(entry.expires_at) }) }}
          </p>
        </div>
        <div class="entry-side">
          <p :class="['entry-amount', entry.action === 'add' ? 'is-add' : 'is-burn']">
            {{ entry.action === 'add' ? '+' : '' }}{{ numberWithCommas(entry.amount) }}
          </p>
          <p class="entry-date">{{ formatDate(entry.created) }}</p>
        </div>
      </div>
    </div>

    <MButton
      v-if="items.length < count"
      outline
      class="load-more"
      :disabled="isLoading"
      @click="loadMore"
    >
      {{ t('coinHistory.loadMore') }}
    </MButton>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import walletApi from '@/api/wallet'
import MButton from '@/components/Form/MButton'
import MiniLoading from '@/components/MiniLoading'
import numberWithCommas from '@/utils/numberWithCommas'

const PAGE_SIZE = 20

export default {
  name: 'CoinHistory',
  components: { MButton, MiniLoading },
  setup() {
    const { t, locale } = useI18n()

    const items = ref([])
    const count = ref(0)
    const totalCoin = ref(0)
    const expiringSoon = ref(null)
    const isLoading = ref(false)

    const fetchPage = async (offset) => {
      isLoading.value = true
      try {
        const response = await walletApi.getCoinHistory({ limit: PAGE_SIZE, offset })
        totalCoin.value = response.total_coin
        expiringSoon.value = response.expiring_soon
        count.value = response.count
        items.value = offset === 0 ? response.results : items.value.concat(response.results)
      }
      finally {
        isLoading.value = false
      }
    }

    const loadMore = () => fetchPage(items.value.length)

    const formatDate = (value) => {
      if (!value) return ''
      return new Date(value).toLocaleDateString(locale.value === 'th' ? 'th-TH' : 'en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    }

    // One line of context under the label, built from the typed detail the API sends.
    const describe = (entry) => {
      const d = entry.detail || {}
      if (entry.type === 'clinic_visit') {
        const parts = []
        if (d.branch_name) parts.push(d.branch_name)
        if (d.total_amount) {
          parts.push(t('coinHistory.visitSpend', { amount: numberWithCommas(d.total_amount) }))
        }
        return parts.join(' · ')
      }
      if (d.coupon_code) return t('coinHistory.couponRef', { code: d.coupon_code })
      if (entry.order_id) return t('coinHistory.orderRef', { id: entry.order_id })
      return ''
    }

    onMounted(() => fetchPage(0))

    return {
      t,
      items,
      count,
      totalCoin,
      expiringSoon,
      isLoading,
      loadMore,
      formatDate,
      describe,
      numberWithCommas,
    }
  },
}
</script>

<style lang="scss" scoped>
/* #app is a flex column, so `margin: 0 auto` here would collapse the page to
   fit-content on the cross axis. Match the other pages: full width, and let
   .responsive-p supply the desktop side padding. */
/* On desktop #app is a flex column whose next siblings are the footers (see
   App.vue). The page must NOT set height/min-height:100% here: with the default
   flex-shrink:1 the flex item is allowed to be shorter than its content, so a
   long history overflows *past* the box and the footers end up buried under the
   list. `flex: 1 0 auto` (grow to fill the viewport on short histories,
   shrink:0 so it never compresses below its content) keeps the page content-
   sized and lets the footers flow after the last entry. Verified with a
   headless-browser repro of the #app scroll container. */
.coin-history {
  width: 100%;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  padding: 16px;
  padding-bottom: 76px;
}

.back {
  color: var(--secondary-color);
  font-size: 1.2rem;
  padding: 12px 0;
  cursor: pointer;
}

.title {
  font-size: 1.6rem;
  font-weight: bold;
  color: var(--secondary-color);
  margin-bottom: 16px;
}

.balance-card {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #E4E4E4;
  border-radius: 10px;
  padding: 16px;
  background-color: #fff;
  flex-shrink: 0;
}

.balance-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.balance-amount {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--secondary-color);
}

.balance-label {
  font-size: 1.1rem;
  color: #6A6A6A;
}

.expiring-soon {
  margin-top: 10px;
  font-size: 1.1rem;
  color: #FD6868;
}

.empty {
  margin-top: 32px;
  text-align: center;
  color: #6A6A6A;
  font-size: 1.2rem;
}

.entries {
  margin-top: 18px;
}

.entry {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 4px;
  border-bottom: 1px solid #EFEFEF;
}

/* Let the label wrap, but never let the amount column be squeezed. */
.entry-main {
  min-width: 0;
  flex: 1 1 auto;
}

.entry-label {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--secondary-color);
}

.entry-detail {
  font-size: 1.05rem;
  color: #6A6A6A;
  margin-top: 2px;
}

.entry-expiry {
  font-size: 1rem;
  color: #9A9A9A;
  margin-top: 2px;
}

.entry-side {
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
}

.entry-amount {
  font-size: 1.25rem;
  font-weight: bold;

  &.is-add {
    color: #16A34A;
  }

  &.is-burn {
    color: #FD6868;
  }
}

.entry-date {
  font-size: 1rem;
  color: #9A9A9A;
  margin-top: 2px;
}

.load-more {
  width: 100%;
  margin-top: 20px;
}
</style>
